import React, { useState, useEffect } from 'react';
import { useAuth } from 'shared/context/AuthContext';
import { useDarkMode } from 'shared/context/DarkModeContext';
import useWindowSize from 'shared/hooks/useWindowSize';
import API, { getImagenUrl } from 'shared/api';
import AsistenteIA from '../AsistenteIA';
import AttendanceWidget from '../Asistencia/AttendanceWidget';
import CampanaNotificaciones from 'shared/components/CampanaNotificaciones';
import AvisosBanner from 'shared/components/AvisosBanner';
import { useNotificaciones } from 'shared/context/NotificacionesContext';
import {
  FiMenu,
  FiGrid,
  FiSettings,
  FiLogOut,
  FiChevronLeft,
  FiUsers,
  FiMapPin,
  FiX,
  FiBriefcase,
  FiDollarSign,
  FiCreditCard,
  FiPackage,
  FiCpu,
  FiShoppingCart,
  FiBarChart2,
  FiRefreshCw,
  FiUserCheck,
  FiClock,
  FiLayers,
  FiLock,
  FiMoon,
  FiSun,
  FiActivity,
  FiBell,
  FiList,
  FiPercent,
  FiShoppingBag,
} from 'react-icons/fi';

function Layout({
  children,
  currentPage,
  onPageChange,
  isSuperAdmin,
  empresaConfig,
}) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [planProximoVencer, setPlanProximoVencer] = useState(false);
  const [planVencido, setPlanVencido] = useState(false);
  const [planInactivo, setPlanInactivo] = useState(false);
  const {
    usuario,
    logout,
    isAdmin,
    isSupervisor,
    isAsesor,
    isPlanExterno,
    getRolLabel,
    getSedeInfo,
    tienePermiso,
    isTipoEmpresa,
  } = useAuth();
  const { darkMode, toggleDarkMode, COL } = useDarkMode();
  const { noLeidasPorPage, marcarLeidasPorPage } = useNotificaciones();
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const sedeInfo = getSedeInfo();

  useEffect(() => {
    if ((!isAdmin() && !tienePermiso('plan.ver')) || isSuperAdmin) return;
    API.get('/suscripciones/mi-suscripcion')
      .then((res) => {
        if (res.data.status === 'success') {
          const d = res.data.data;
          // Refrescar plan_tipo si cambio en backend (ej. tras aprobacion de cambio de plan):
          // el localStorage se fija en login y queda viejo. Sincroniza y recarga una vez.
          if (d && d.plan_tipo && d.plan_tipo !== usuario?.plan_tipo) {
            try {
              const u = JSON.parse(localStorage.getItem('usuario') || '{}');
              u.plan_tipo = d.plan_tipo;
              localStorage.setItem('usuario', JSON.stringify(u));
            } catch (e) {
              /* noop */
            }
            window.location.reload();
            return;
          }
          if (!d || d.estado === 'sin_plan') {
            setPlanInactivo(true);
            setPlanProximoVencer(false);
            setPlanVencido(false);
          } else {
            setPlanInactivo(false);
            const dias = d.dias_restantes;
            setPlanProximoVencer(dias !== null && dias !== undefined && dias >= 0 && dias <= 7);
            setPlanVencido(dias !== null && dias !== undefined && dias < 0);
          }
        }
      })
      .catch((e) => console.error('Error cargando plan:', e?.message || e));
  }, [usuario, isSuperAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      window.__agoraxDeferredPrompt = e;
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const superAdminItems = [
    { id: 'dashboard', icon: FiGrid, label: 'Dashboard' },
    { id: 'empresas', icon: FiBriefcase, label: 'Empresas' },
    { id: 'usuarios', icon: FiUsers, label: 'Usuarios' },
    { id: 'planes', icon: FiLayers, label: 'Planes' },
    { id: 'pagos', icon: FiDollarSign, label: 'Pagos' },
    { id: 'solicitudes', icon: FiRefreshCw, label: 'Cambios de plan' },
    { id: 'metrics', icon: FiActivity, label: 'Métricas' },
    { id: 'avisos', icon: FiBell, label: 'Avisos' },
  ];

  const workerItems = [
    {
      id: 'chats',
      icon: FiGrid,
      label: 'Panel',
      permiso: ['chat.acceder', 'pedidos.armado', 'pedidos.cocinero', 'pedidos.delivery'],
    },
    {
      id: 'dashboard',
      icon: FiBarChart2,
      label: 'Dashboard',
      adminOnly: true,
      permiso: 'dashboard.ver',
    },
    {
      id: 'reportes',
      icon: FiList,
      label: 'Reportes',
      adminOnly: true,
      permiso: [
        'dashboard.ver',
        'reportes.ventas',
        'reportes.inventario',
        'reportes.produccion',
        'reportes.cierres',
        'reportes.clientes',
        'reportes.usuarios',
        'reportes.ia',
      ],
    },
    {
      id: 'inventario',
      icon: FiPackage,
      label: 'Inventario',
      adminOnly: true,
      permiso: 'inventario.ver',
    },
    {
      id: 'compras',
      icon: FiShoppingBag,
      label: 'Compras',
      adminOnly: true,
      permiso: 'compras.ver',
    },
    {
      id: 'tesoreria',
      icon: FiDollarSign,
      label: 'Tesorería',
      adminOnly: true,
      permiso: 'tesoreria.ver',
    },
    {
      id: 'produccion',
      icon: FiLayers,
      label: 'Producción',
      adminOnly: true,
      excluirTipos: ['importador', 'general'],
      permiso: 'produccion.ver',
    },
    {
      id: 'pedidos-tienda',
      icon: FiShoppingCart,
      label: 'Pedidos Tienda',
      requierePlanInterno: true,
      excluirTipos: ['importador', 'general'],
      permiso: [
        'pedidos_tienda.salon_ver',
        'pedidos_tienda.pedido_ver',
        'pedidos_tienda.espera_ver',
        'pedidos_tienda.historial_ver',
      ],
    },

    {
      id: 'cierre-caja',
      icon: FiLock,
      label: 'Cierre de Caja',
      requierePlanInterno: true,
      excluirTipos: ['importador', 'general'],
      permiso: 'cierre_caja.ver',
    },
    {
      id: 'cierre-caja-admin',
      icon: FiDollarSign,
      label: 'Cajas Activas',
      adminOnly: true,
      requierePlanInterno: true,
      excluirTipos: ['importador', 'general'],
      permiso: 'cierre_caja.admin',
    },
    {
      id: 'clientes',
      icon: FiUserCheck,
      label: 'Clientes',
      adminOnly: true,
      permiso: 'clientes.ver',
    },
    {
      id: 'vendedores',
      icon: FiBriefcase,
      label: 'Vendedores',
      adminOrSupervisor: true,
      soloTipos: ['importador'],
      permiso: 'vendedores.ver',
    },
    {
      id: 'vendedores-panel',
      icon: FiPercent,
      label: 'Mis Comisiones',
      soloVendedor: true,
      soloTipos: ['importador'],
      permiso: 'vendedores.panel',
    },
    {
      id: 'usuarios',
      icon: FiUsers,
      label: 'Usuarios',
      adminOrSupervisor: true,
      permiso: 'usuarios.ver',
    },
    { id: 'sedes', icon: FiMapPin, label: 'Sedes', adminOrSupervisor: true, permiso: 'sedes.ver' },
    { id: 'asistencia', icon: FiClock, label: 'Asistencia', permiso: 'asistencia.acceder' },
    { id: 'pago', icon: FiCreditCard, label: 'Plan', adminOnly: true, permiso: 'plan.ver' },
    { id: 'bot-ia', icon: FiCpu, label: 'Bot IA', adminOnly: true, permiso: 'bot.ver' },
    { id: 'configuracion', icon: FiSettings, label: 'Configuración', permiso: 'config.perfil' },
  ];

  const allMenuItems = isSuperAdmin ? superAdminItems : workerItems;
  const menuItems = allMenuItems.filter((item) => {
    if (isSuperAdmin) return true;
    const rol = usuario?.rol;
    if (item.requierePlanInterno && isPlanExterno()) return false;
    if (item.excluirTipos && isTipoEmpresa && item.excluirTipos.some((t) => isTipoEmpresa(t)))
      return false;
    if (item.soloTipos && isTipoEmpresa && !item.soloTipos.some((t) => isTipoEmpresa(t)))
      return false;
    if (item.soloVendedor && rol !== 'vendedor') return false;
    if (item.permiso) {
      const permisos = Array.isArray(item.permiso) ? item.permiso : [item.permiso];
      if (!permisos.some((p) => tienePermiso(p))) return false;
    }
    return true;
  });

  const empColor = empresaConfig?.color_primario || '#0B1A4A';
  const sidebarBg = isSuperAdmin ? '#1a1a2e' : empColor;
  const empresaLogo = empresaConfig?.logo_url
    ? getImagenUrl(empresaConfig.logo_url)
    : '/logo-agorax.jpeg';
  const empresaNombre = empresaConfig?.empresa_nombre || 'Agorax';

  useEffect(() => {
    if (!empresaConfig) return;
    if (empresaConfig.empresa_nombre) {
      document.title = `${empresaConfig.empresa_nombre} - Admin`;
    }
    const favicon = document.querySelector('link[rel="icon"]');
    if (favicon && empresaConfig.logo_url) {
      favicon.setAttribute('href', getImagenUrl(empresaConfig.logo_url));
    }
    const appleIcon = document.querySelector('link[rel="apple-touch-icon"]');
    if (appleIcon && empresaConfig.logo_url) {
      appleIcon.setAttribute('href', getImagenUrl(empresaConfig.logo_url));
    }
    const metaTheme = document.querySelector('meta[name="theme-color"]');
    if (metaTheme) metaTheme.setAttribute('content', empColor);
  }, [empresaConfig, empColor]);

  const handleMenuClick = (id) => {
    onPageChange(id);
    marcarLeidasPorPage(id);
    if (isMobile) setSidebarOpen(false);
  };

  return (
    <div style={styles.container}>
      <style>{`
        .layout-sidebar-scroll { overflow-y: auto; overflow-x: hidden; }
        .layout-sidebar-scroll::-webkit-scrollbar { width: 5px; }
        .layout-sidebar-scroll::-webkit-scrollbar-track { background: transparent; }
        .layout-sidebar-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 10px; }
        .layout-sidebar-scroll::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.25); }
        .layout-sidebar-scroll { scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.15) transparent; }
        .layout-content-scroll { overflow-y: auto; overflow-x: hidden; }
        .layout-content-scroll::-webkit-scrollbar { width: 6px; }
        .layout-content-scroll::-webkit-scrollbar-track { background: transparent; }
        .layout-content-scroll::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.12); border-radius: 10px; }
        .layout-content-scroll::-webkit-scrollbar-thumb:hover { background: rgba(0,0,0,0.22); }
        .layout-content-scroll { scrollbar-width: thin; scrollbar-color: rgba(0,0,0,0.12) transparent; }
        @media (max-width: 768px) {
          .layout-main-content { padding: 0 !important; }
          .layout-header { padding: 10px 12px !important; flex-wrap: wrap; gap: 8px !important; }
          .layout-header-title { font-size: 16px !important; }
          .layout-header-sede { font-size: 11px !important; padding: 3px 8px !important; }
          .layout-content-area { padding: 0 !important; }
        }
      `}</style>
      {/* Overlay para mobile */}
      {isMobile && sidebarOpen && (
        <div style={styles.overlay} onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <div
        style={{
          ...styles.sidebar,
          backgroundColor: sidebarBg,
          width: isMobile ? '260px' : sidebarOpen ? '260px' : '70px',
          transform: isMobile ? (sidebarOpen ? 'translateX(0)' : 'translateX(-100%)') : 'none',
          position: isMobile ? 'fixed' : 'relative',
          zIndex: isMobile ? 1100 : 1,
          height: isMobile ? '100vh' : '100%',
          left: 0,
          top: 0,
        }}
      >
        <div style={styles.logoContainer}>
          <div style={styles.logoIcon}>
            <img src={empresaLogo} alt={empresaNombre} style={styles.logoImg} />
          </div>
          {(sidebarOpen || isMobile) && (
            <div style={styles.logoText}>
              <div style={styles.logoTitle}>{empresaNombre}</div>
              <div style={styles.logoEmpresa}>Panel Admin</div>
            </div>
          )}
          {isMobile ? (
            <button onClick={() => setSidebarOpen(false)} style={styles.toggleBtn}>
              <FiX size={20} />
            </button>
          ) : (
            <button onClick={() => setSidebarOpen(!sidebarOpen)} style={styles.toggleBtn}>
              {sidebarOpen ? <FiChevronLeft size={20} /> : <FiMenu size={20} />}
            </button>
          )}
        </div>

        <div className="layout-sidebar-scroll" style={styles.menu}>
          {menuItems.map((item) => (
            <div
              key={item.id}
              onClick={() => handleMenuClick(item.id)}
              style={{
                ...styles.menuItem,
                padding: sidebarOpen || isMobile ? '12px 16px' : '12px 10px',
                margin: sidebarOpen || isMobile ? '4px 10px' : '4px 6px',
                backgroundColor: currentPage === item.id ? 'rgba(255,255,255,0.12)' : 'transparent',
                color: currentPage === item.id ? '#FFFFFF' : 'rgba(255,255,255,0.6)',
              }}
            >
              <div style={{ position: 'relative' }}>
                <item.icon
                  size={22}
                  style={{
                    ...styles.menuIcon,
                    marginRight: sidebarOpen || isMobile ? '12px' : '0px',
                    color: currentPage === item.id ? '#FFFFFF' : 'rgba(255,255,255,0.5)',
                  }}
                />
                {item.id === 'pago' && planInactivo && (
                  <div
                    style={{
                      ...styles.warningDot,
                      backgroundColor: '#888',
                      border: '2px solid ' + sidebarBg,
                    }}
                  />
                )}
                {item.id === 'pago' && planVencido && !planInactivo && (
                  <div
                    style={{
                      ...styles.warningDot,
                      backgroundColor: '#c62828',
                      border: '2px solid ' + sidebarBg,
                    }}
                  />
                )}
                {item.id === 'pago' && planProximoVencer && !planVencido && !planInactivo && (
                  <div style={styles.warningDot} />
                )}
                {noLeidasPorPage[item.id] > 0 && (
                  <div
                    style={{
                      ...styles.warningDot,
                      backgroundColor: '#ea4335',
                      border: '2px solid ' + sidebarBg,
                      top: '-4px',
                      right: '-4px',
                    }}
                  />
                )}
              </div>
              {(sidebarOpen || isMobile) && (
                <span style={styles.menuLabel}>
                  {item.label}
                  {noLeidasPorPage[item.id] > 0 && (
                    <span style={styles.notifBadge}>
                      {noLeidasPorPage[item.id] > 9 ? '9+' : noLeidasPorPage[item.id]}
                    </span>
                  )}
                  {item.id === 'pago' && planInactivo && (
                    <span
                      style={{
                        ...styles.warningBadge,
                        color: '#888',
                        backgroundColor: 'rgba(136,136,136,0.15)',
                      }}
                    >
                      Inactivo
                    </span>
                  )}
                  {item.id === 'pago' && planVencido && !planInactivo && (
                    <span
                      style={{
                        ...styles.warningBadge,
                        color: '#c62828',
                        backgroundColor: 'rgba(198,40,40,0.15)',
                      }}
                    >
                      Vencido
                    </span>
                  )}
                  {item.id === 'pago' && planProximoVencer && !planVencido && !planInactivo && (
                    <span style={styles.warningBadge}>Proximo a vencer</span>
                  )}
                </span>
              )}
            </div>
          ))}
        </div>

        <div style={styles.userSection}>
          <div
            style={{
              ...styles.userAvatar,
              backgroundColor: isAdmin() ? '#ea4335' : isSupervisor() ? '#fbbc04' : '#1a73e8',
            }}
          >
            {usuario?.nombre?.charAt(0) || 'A'}
          </div>
          {(sidebarOpen || isMobile) && (
            <div style={styles.userInfo}>
              <div style={styles.userName}>{usuario?.nombre || 'Usuario'}</div>
              <div style={styles.userRole}>
                {getRolLabel()}
                {sedeInfo && <span style={styles.sedeBadge}> - {sedeInfo.nombre}</span>}
              </div>
            </div>
          )}
          {(sidebarOpen || isMobile) && (
            <button onClick={logout} style={styles.logoutBtn}>
              <FiLogOut size={20} />
            </button>
          )}
        </div>
      </div>

      {/* Contenido principal */}
      <div className="layout-main-content" style={styles.mainContent}>
        <div className="layout-header" style={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {isMobile && (
              <button onClick={() => setSidebarOpen(true)} style={styles.mobileMenuBtn}>
                <FiMenu size={20} />
              </button>
            )}
            <h2 className="layout-header-title" style={styles.headerTitle}>
              {menuItems.find((item) => item.id === currentPage)?.label || 'Panel'}
            </h2>
          </div>
          {sedeInfo && isAsesor() && (
            <span className="layout-header-sede" style={styles.headerSede}>
              Sede: {sedeInfo.nombre}
            </span>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CampanaNotificaciones onVer={onPageChange} color="var(--agx-header-text)" />
            <button
              onClick={toggleDarkMode}
              title={darkMode ? 'Modo claro' : 'Modo oscuro'}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '6px',
                borderRadius: '8px',
                display: 'flex',
                color: COL.muted,
                fontSize: '18px',
                transition: 'color 0.2s',
              }}
            >
              {darkMode ? <FiSun size={18} /> : <FiMoon size={18} />}
            </button>
            {!isSuperAdmin && <AttendanceWidget />}
            {(isAdmin() || tienePermiso('inventario.ver')) && !isSuperAdmin && (
              <AsistenteIA pagina={currentPage} />
            )}
          </div>
        </div>
        <div className="layout-content-area layout-content-scroll" style={styles.content}>
          {(isAdmin() || tienePermiso('plan.ver')) && planInactivo && (
            <div style={styles.inactiveBanner}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '14px', fontWeight: 600, color: '#664d00' }}>
                  Tu sitio esta inactivo
                </span>
                <span style={{ fontSize: '13px', color: '#806600' }}>
                  Realiza tu primer pago en la sección Plan para activar tu sitio web.
                </span>
              </div>
              <button
                onClick={() => onPageChange('pago')}
                style={{
                  padding: '8px 18px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 600,
                  background: '#e67700',
                  color: '#fff',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                Ir a Plan y Pagos
              </button>
            </div>
          )}
          {!isSuperAdmin && <AvisosBanner />}
          {children}
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    height: '100vh',
    width: '100vw',
    backgroundColor: 'var(--agx-bg)',
    overflow: 'hidden',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(11,26,74,0.4)',
    zIndex: 1000,
    transition: 'opacity 0.3s',
  },
  sidebar: {
    backgroundColor: '#0B1A4A',
    borderRight: 'none',
    display: 'flex',
    flexDirection: 'column',
    transition: 'width 0.3s ease, transform 0.3s ease',
    boxShadow: '4px 0 24px rgba(11,26,74,0.15)',
    flexShrink: 0,
    color: '#FFFFFF',
  },
  logoContainer: {
    padding: '20px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  logoIcon: { display: 'flex', alignItems: 'center' },
  logoImg: { width: '36px', height: '36px', borderRadius: '10px', objectFit: 'contain', backgroundColor: '#fff' },
  logoText: { flex: 1, marginLeft: '12px' },
  logoTitle: { fontSize: '18px', fontWeight: 700, color: '#FFFFFF', letterSpacing: '-0.3px' },
  logoEmpresa: { fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '2px' },
  toggleBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'rgba(255,255,255,0.6)',
    display: 'flex',
    alignItems: 'center',
    padding: '4px',
  },
  menu: { flex: 1, paddingTop: '20px' },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    cursor: 'pointer',
    borderRadius: '12px',
    margin: '4px 10px',
    transition: 'all 0.2s',
    color: 'rgba(255,255,255,0.6)',
  },
  menuIcon: {},
  menuLabel: {
    fontSize: '14px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  warningDot: {
    position: 'absolute',
    top: '-2px',
    right: '2px',
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#ff6b00',
    border: '2px solid #0B1A4A',
  },
  warningBadge: {
    fontSize: '10px',
    fontWeight: 600,
    color: '#ff6b00',
    backgroundColor: 'rgba(255,107,0,0.15)',
    padding: '2px 6px',
    borderRadius: '6px',
    whiteSpace: 'nowrap',
  },
  notifBadge: {
    fontSize: '10px',
    fontWeight: 700,
    color: '#fff',
    backgroundColor: '#ea4335',
    minWidth: '18px',
    height: '18px',
    borderRadius: '9px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 5px',
    marginLeft: '4px',
  },
  userSection: {
    padding: '16px',
    borderTop: '1px solid rgba(255,255,255,0.08)',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  userAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '16px',
    flexShrink: 0,
  },
  userInfo: { flex: 1 },
  userName: { fontSize: '14px', fontWeight: 600, color: '#FFFFFF' },
  userRole: {
    fontSize: '11px',
    color: 'rgba(255,255,255,0.5)',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  sedeBadge: {
    backgroundColor: 'rgba(255,255,255,0.1)',
    color: 'rgba(255,255,255,0.7)',
    padding: '2px 8px',
    borderRadius: '8px',
    fontSize: '10px',
  },
  logoutBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'rgba(255,255,255,0.5)',
    display: 'flex',
    alignItems: 'center',
  },
  mobileMenuBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--agx-pri)',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
  },
  mainContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minWidth: 0,
  },
  header: {
    backgroundColor: 'var(--agx-header-bg)',
    padding: '14px 20px',
    borderBottom: '1px solid var(--agx-header-border)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexShrink: 0,
  },
  headerTitle: { fontSize: '18px', fontWeight: 700, color: 'var(--agx-header-text)', margin: 0 },
  headerSede: {
    fontSize: '12px',
    color: '#2952CC',
    backgroundColor: 'rgba(41,82,204,0.08)',
    padding: '4px 12px',
    borderRadius: '12px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  content: { flex: 1 },
  inactiveBanner: {
    background: '#fff8e6',
    borderBottom: '2px solid #ffd966',
    padding: '12px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
};

export default Layout;
